import { useCallback, useEffect, useState } from "react";

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

function getFullscreenElement(): Element | null {
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
  };
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

function isFullscreenActive(): boolean {
  return getFullscreenElement() !== null;
}

async function requestAppFullscreen(): Promise<void> {
  const el = document.documentElement as FullscreenElement;
  if (el.requestFullscreen) {
    await el.requestFullscreen();
    return;
  }
  if (el.webkitRequestFullscreen) {
    await el.webkitRequestFullscreen();
  }
}

async function exitAppFullscreen(): Promise<void> {
  const doc = document as Document & {
    webkitExitFullscreen?: () => Promise<void> | void;
  };
  if (doc.exitFullscreen) {
    await doc.exitFullscreen();
    return;
  }
  if (doc.webkitExitFullscreen) {
    await doc.webkitExitFullscreen();
  }
}

function isFullscreenSupported(): boolean {
  const el = document.documentElement as FullscreenElement;
  return Boolean(el.requestFullscreen ?? el.webkitRequestFullscreen);
}

/**
 * Browser Fullscreen API for kiosk-style shop floor tablets.
 * Falls back gracefully when unsupported (e.g. some mobile browsers).
 */
export function useFullscreen() {
  const [active, setActive] = useState(() => isFullscreenActive());
  const [supported] = useState(() => isFullscreenSupported());

  useEffect(() => {
    const sync = () => setActive(isFullscreenActive());
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggle = useCallback(async () => {
    if (!supported) return;
    try {
      if (isFullscreenActive()) {
        await exitAppFullscreen();
      } else {
        await requestAppFullscreen();
      }
    } catch {
      // User gesture required, or browser denied — ignore.
    }
  }, [supported]);

  return { active, supported, toggle };
}
